import './styles.sass';

import React, { useEffect, useMemo, useRef } from 'react';

import * as QueryParams from 'components/Explorer/models/query_params';
import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import ResearchOutput from 'components/Explorer/models/research_output';
import PageTitle from 'components/Explorer/PageTitle';
import SummaryBlock from 'components/Explorer/ContentPanel/SummaryBlock';
import CurrentsCard, { SENTIMENT_KEYS } from './CurrentsCard';

const FEED_LIMIT = 96;

const DEFAULT_TIMEFRAME = '1w';

const TIMEFRAMES = ['1d', '1w', '1m', '3m', '6m', '1y'];

const TIMEFRAME_TO_MENTIONS_SORT = {
  '1d': 'mentions_1d',
  '1w': 'mentions_1w',
  '1m': 'mentions_1m',
  '3m': 'mentions_3m',
  '6m': 'mentions_6m',
  '1y': 'mentions_1y'
};

const SORT_OPTIONS = [{ key: 'mentions' }, { key: 'score' }, { key: 'publication_date' }];

const SOURCE_FILTER_KEY = 'mention_sources_types[]';

// primarySort: the source-specific sort key used when "Most mentioned" is selected.
// color: dark tone from the Altmetric source palette for mention count display.
const SOURCE_CATEGORIES = [
  { key: 'all', types: null, primarySort: null, color: '#1565c0' },
  { key: 'social', types: ['type:tw', 'type:bsk', 'type:rdt'], primarySort: 'twitter', color: '#2F90B9' },
  { key: 'news', types: ['type:msm'], primarySort: 'msm', color: '#B60000' },
  { key: 'policy', types: ['type:policy', 'type:guideline'], primarySort: 'policy', color: '#270a63' },
  { key: 'blogs', types: ['type:blog'], primarySort: 'blogs', color: '#e89500' },
  { key: 'podcasts', types: ['type:podcast'], primarySort: 'podcasts', color: '#90089C' }
];

// Resolve the "Most mentioned" sort order based on current source + timeframe.
// Source-specific sort trumps timeframe-based total mentions.
export function resolveMentionSort(sourceKey, timeframe) {
  const category = SOURCE_CATEGORIES.find((c) => c.key === sourceKey);
  if (category?.primarySort) return category.primarySort;
  return TIMEFRAME_TO_MENTIONS_SORT[timeframe] || 'mentions_1w';
}

export function activeSourceCategory(filters) {
  const raw = filters?.[SOURCE_FILTER_KEY];
  if (!raw) return 'all';

  const current = Array.isArray(raw) ? raw : [raw];

  const match = SOURCE_CATEGORIES.find(
    (cat) => cat.types && cat.types.length === current.length && cat.types.every((t) => current.includes(t))
  );
  return match ? match.key : 'custom';
}

const Currents = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();
  const rootRef = useRef(null);

  // Default to "mentioned this week", all sources, sorted by most discussed —
  // surfaces what's getting attention right now, not all-time high scorers.
  // Replace (not push) so the user can still navigate back to where they came from.
  useEffect(() => {
    const needsTimeframe = !params.filters.timeframe;
    const needsSort = !params.sortOrder || params.sortOrder === 'score';
    if (!needsTimeframe && !needsSort) return;

    let updated = params;
    if (needsTimeframe) {
      updated = QueryParams.withFilters(updated, { ...updated.filters, timeframe: DEFAULT_TIMEFRAME });
    }
    if (needsSort) {
      const sourceKey = activeSourceCategory(updated.filters);
      updated = QueryParams.withSortOrder(updated, resolveMentionSort(sourceKey, DEFAULT_TIMEFRAME));
    }
    navigateParams(updated, { pathname: '/currents', replace: true });
  }, []);

  const { data, loading } = useExplorerData('research_outputs', params, {
    reloadOn: ['filters', 'sortOrder']
  });

  useEffect(() => {
    if (loading === Loading.IN_PROGRESS) {
      const wrapper = rootRef.current?.closest('.Explorer-ContentPanel');
      if (wrapper) wrapper.scrollTop = 0;
    }
  }, [loading]);

  const outputs = useMemo(
    () => (data?.outputs ? data.outputs.slice(0, FEED_LIMIT).map((r) => new ResearchOutput(r)) : []),
    [data]
  );

  const isLoading = loading === Loading.NOT_STARTED || loading === Loading.IN_PROGRESS;
  const loadingFailed = loading === Loading.ERROR;

  const currentTimeframe = params.filters?.timeframe || DEFAULT_TIMEFRAME;
  const currentSource = activeSourceCategory(params.filters);
  const currentSortKey = (() => {
    const order = params.sortOrder || 'score';
    if (order === 'publication_date') return 'publication_date';
    if (order === 'score') return 'score';
    return 'mentions';
  })();

  const goTo = (next) => navigateParams(next, { pathname: '/currents' });

  const handleTimeframeChange = (timeframe) => {
    Analytics.trackEvent('currents-change-timeframe', { timeframe });
    let updated = QueryParams.withFilters(params, { ...params.filters, timeframe });

    // If sorted by mentions, re-resolve for the new timeframe
    if (currentSortKey === 'mentions') {
      const sourceKey = activeSourceCategory(params.filters);
      updated = QueryParams.withSortOrder(updated, resolveMentionSort(sourceKey, timeframe));
    }

    goTo(updated);
  };

  const handleCardClick = (output) => {
    Analytics.trackEvent('currents-open-details-page', { outputId: output.id });
    navigateParams(QueryParams.withShowDetails(params, output.id));
  };

  const handleSourceChange = (category) => {
    Analytics.trackEvent('currents-change-source', { source: category.key });
    const newFilters = { ...params.filters };
    if (category.types) {
      newFilters[SOURCE_FILTER_KEY] = category.types;
    } else {
      delete newFilters[SOURCE_FILTER_KEY];
    }

    let updated = QueryParams.withFilters(params, newFilters);

    if (currentSortKey === 'mentions') {
      const tf = params.filters.timeframe || DEFAULT_TIMEFRAME;
      updated = QueryParams.withSortOrder(updated, resolveMentionSort(category.key, tf));
    }

    goTo(updated);
  };

  const handleSortChange = (sortOption) => {
    let order;
    if (sortOption.key === 'mentions') {
      const sourceKey = activeSourceCategory(params.filters);
      const tf = params.filters.timeframe || DEFAULT_TIMEFRAME;
      order = resolveMentionSort(sourceKey, tf);
    } else {
      order = sortOption.key;
    }

    Analytics.trackEvent('currents-change-sort', { sort: order });
    goTo(QueryParams.withSortOrder(params, order));
  };

  const activeCategory = SOURCE_CATEGORIES.find((c) => c.key === currentSource);
  const mentionContext =
    currentSortKey === 'mentions' ? I18n.t(`Explorer.Currents.mention_labels.${currentSource}`) : null;
  const mentionColor = currentSortKey === 'mentions' ? activeCategory?.color : null;

  return (
    <div className="Explorer-Currents" ref={rootRef}>
      <PageTitle metaTitle={I18n.t('Explorer.TabBar.currents')} />

      <SummaryBlock>
        <div className="Explorer-Currents-intro">
          <h2 className="Explorer-Currents-title">
            <span className="Explorer-Currents-title-icon" aria-hidden="true">
              {I18n.t('Explorer.Currents.title_icon')}
            </span>
            {I18n.t('Explorer.Currents.title')}
          </h2>
          <p className="Explorer-Currents-subtitle">{I18n.t('Explorer.Currents.subtitle')}</p>
        </div>
      </SummaryBlock>

      <div className="Explorer-Currents-controls">
        <div
          className="Explorer-Currents-timeframes"
          role="group"
          aria-label={I18n.t('Explorer.Currents.timeframe_label')}
        >
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              className={`Explorer-Currents-filter-btn ${tf === currentTimeframe ? 'active' : ''}`}
              onClick={() => handleTimeframeChange(tf)}
              aria-pressed={tf === currentTimeframe}
            >
              {I18n.t(`Explorer.Currents.timeframes.${tf}`)}
            </button>
          ))}
        </div>

        <div className="Explorer-Currents-sources" role="group" aria-label={I18n.t('Explorer.Currents.source_label')}>
          {SOURCE_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              className={`Explorer-Currents-filter-btn ${cat.key === currentSource ? 'active' : ''}`}
              onClick={() => handleSourceChange(cat)}
              aria-pressed={cat.key === currentSource}
            >
              {I18n.t(`Explorer.Currents.sources.${cat.key}`)}
            </button>
          ))}
        </div>

        <div className="Explorer-Outputs-SortBy">
          <label className="label" htmlFor="currents-sort-select">
            {I18n.t('research_outputs.sort_by')}:
          </label>{' '}
          <select
            id="currents-sort-select"
            value={currentSortKey}
            onChange={(e) => handleSortChange({ key: e.target.value })}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {I18n.t(`Explorer.Currents.sorts.${opt.key}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isLoading &&
        outputs.length > 0 &&
        (() => {
          const totals = {};
          SENTIMENT_KEYS.forEach((k) => (totals[k] = 0));
          outputs.forEach((o) => {
            if (!o.sentiment) return;
            SENTIMENT_KEYS.forEach((k) => (totals[k] += o.sentiment[k] || 0));
          });
          const total = SENTIMENT_KEYS.reduce((sum, k) => sum + totals[k], 0);
          if (total === 0) return null;
          const pcts = {};
          SENTIMENT_KEYS.forEach((k) => (pcts[k] = Math.round((totals[k] / total) * 100)));
          const positivePct = pcts.neutral_positive + pcts.positive + pcts.strong_positive;
          return (
            <div className="Explorer-Currents-sentiment">
              <span className="Explorer-Currents-sentiment-label">{I18n.t('Explorer.Currents.sentiment.title')}</span>
              <span className="Explorer-Currents-sentiment-bar">
                {SENTIMENT_KEYS.map((k) =>
                  pcts[k] > 0 ? (
                    <span key={k} className={`sentiment-segment sentiment-${k}`} style={{ width: `${pcts[k]}%` }} />
                  ) : null
                )}
              </span>
              <span className="Explorer-Currents-sentiment-summary">
                {positivePct}% {I18n.t('Explorer.Currents.sentiment.positive')}
              </span>
            </div>
          );
        })()}

      <div className="Explorer-Currents-feed">
        {isLoading && <div className="Explorer-Currents-loading-spinner" />}

        {!isLoading && loadingFailed && (
          <div className="Explorer-Currents-error">{I18n.t('Explorer.Currents.error')}</div>
        )}

        {!isLoading && !loadingFailed && outputs.length === 0 && (
          <div className="Explorer-Currents-empty">{I18n.t('Explorer.Currents.empty')}</div>
        )}

        {!isLoading && !loadingFailed && outputs.length > 0 && (
          <div className="Explorer-Currents-cards">
            {outputs.map((output, index) => (
              <CurrentsCard
                key={output.id}
                rank={index + 1}
                output={output}
                onClick={handleCardClick}
                mentionContext={mentionContext}
                mentionColor={mentionColor}
                timeframe={currentTimeframe}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Currents;
